import { router } from "@inertiajs/react"
import { useCallback } from "react"

import { crewPath } from "@/routes"
import type { CrewFilters, CrewSort } from "@/types/crew"

/**
 * OKS-25 — URL state mutator for the Crew Roster page.
 *
 * Ports the OKS-17 `useCandidateListUrl` pattern with three deltas:
 *   1. Filter set diverges (5 filters: q / location / followers /
 *      tags / joined-date — no status, no country/city split, no
 *      default-country redirect — UX Spec is binding).
 *   2. Single composite `location` filter (per BE TRD v2 + UX Spec
 *      v4): the FE Select option value IS the wire string
 *      (e.g. `"Paris, FR"`); URL emits `?location=Paris%2C+FR`.
 *   3. **`stats` is unfiltered** per PRD §Acceptance — partial reload
 *      `only:` array intentionally OMITS `stats`, so filter
 *      mutations can't even theoretically pull a fresh `stats`
 *      block. This is the page-level canary that backs F10's
 *      "stats-unfiltered" Vitest assertion.
 *
 * **Wire-format** (locked w/ BE — see `types/crew.ts`):
 *   - URL keys: `q`, `location`, `followers_min`, `followers_max`,
 *     `tags[]`, `joined_from`, `joined_to`, `sort`, `dir`, `page`
 *   - Default sort `joined_at desc` is OMITTED from the URL (BE
 *     re-applies it from the SORTABLE_FIELDS / DEFAULT_SORT
 *     defaults). Same idiom as OKS-17 for clean back-button URLs.
 */
const RELOAD_KEYS = [
  "members",
  "pagination",
  "filter_options",
  "filters",
  "sort",
  "is_empty",
  // OKS-28 — segment apply triggers a fresh `applied_segment` (chip
  // + selector value) AND a fresh `crew_segments` selector payload
  // (member counts re-evaluate against the same brand-scoped active
  // relation). `stats` remains EXCLUDED — applying a segment never
  // narrows stats (PRD AC + UX Spec §1).
  "applied_segment",
  "crew_segments",
] as const

/**
 * Build the URL params object from the next filter/sort/page state.
 * Drops nullish + empty-string + empty-array values so the URL stays
 * minimal. Arrays are emitted as `key[]` per Rails / Rack conventions.
 *
 * Exported for direct unit testing — keeps the deltas-vs-defaults
 * normalization rule under explicit Vitest coverage (URL round-trip
 * is the canonical canary against drift between FE and BE
 * `ListQuery#applied`).
 *
 * **OKS-28 — segment-exclusive URL contract** (UX Spec Open-Q6 +
 * QA-Plan E14): when `segmentId` is present, the URL emits
 * `?segment=<id>` AND DROPS every raw filter param (location / tags /
 * followers / joined / search / at_risk). The BE applies the same
 * precedence (segment.rules WIN over ad-hoc filter params), so the FE
 * mirroring keeps the URL canonical-shareable.
 */
export function buildCrewListParams(
  filters: Partial<CrewFilters>,
  sort: Partial<CrewSort>,
  page?: number,
  segmentId?: number | null,
): Record<string, string | string[] | number> {
  const params: Record<string, string | string[] | number> = {}

  // OKS-28 — When a segment is applied, only `?segment=<id>` (+ sort +
  // page) survive on the URL. Raw filter params would be silently
  // overridden BE-side anyway; emitting them here would contradict the
  // shareable-URL invariant. `setSegment(null)` clears the segment AND
  // the filters together (the dispatcher passes `{}` filters in that
  // case).
  if (typeof segmentId === "number") {
    params.segment = segmentId
  } else {
    if (filters.q && filters.q.trim() !== "") params.q = filters.q.trim()
    // OKS-348 — Single composite-string location filter
    // (`?location=City, ISO-2`). The pre-OKS-348 `canonical_location_id`
    // branch is gone.
    if (filters.location && filters.location.trim() !== "") {
      params.location = filters.location.trim()
    }
    if (filters.tags && filters.tags.length > 0) params.tags = filters.tags
    if (typeof filters.followers_min === "number")
      params.followers_min = filters.followers_min
    if (typeof filters.followers_max === "number")
      params.followers_max = filters.followers_max
    if (filters.joined_from && filters.joined_from.trim() !== "")
      params.joined_from = filters.joined_from
    if (filters.joined_to && filters.joined_to.trim() !== "")
      params.joined_to = filters.joined_to
    // OKS-27 — At-risk lifecycle filter. Wire format `?at_risk=1` (NOT
    // `=true`); BE casts via `ActiveModel::Type::Boolean`. Emitting the
    // numeric literal `1` keeps Rails-side parsing deterministic AND
    // avoids the URL string `at_risk=true` (which would still cast
    // truthy but drift from the canary spec). Default `false` is omitted
    // entirely (BE re-applies default).
    if (filters.at_risk === true) params.at_risk = 1
    // OKS-58 — Masterclass-completion rule. Both keys MUST travel as a
    // pair; emit both or neither. The BE eval-time floor
    // (`apply_masterclass_completion`) also requires both to be present
    // + well-formed. Mirroring the pair-invariant on the URL emitter
    // keeps the URL shareable + the Vitest URL canary easy to assert.
    if (
      filters.masterclass_completion_op &&
      typeof filters.masterclass_completion_id === "number"
    ) {
      params.masterclass_completion_op = filters.masterclass_completion_op
      params.masterclass_completion_id = filters.masterclass_completion_id
    }
  }
  // Default sort `joined_at desc` is omitted to keep the URL clean —
  // BE re-applies the default whenever `sort`/`dir` are absent.
  if (sort.field && sort.field !== "joined_at") params.sort = sort.field
  if (sort.dir && sort.dir !== "desc") params.dir = sort.dir
  if (typeof page === "number" && page > 1) params.page = page

  return params
}

interface UseCrewListUrlReturn {
  /** Push a new filter/sort/page state to the URL via Inertia partial reload. */
  push: (
    nextFilters: Partial<CrewFilters>,
    nextSort?: Partial<CrewSort>,
    nextPage?: number,
  ) => void
  /** Set a single filter (keeps other filters + sort, resets page to 1). */
  setFilter: <K extends keyof CrewFilters>(
    key: K,
    value: CrewFilters[K],
  ) => void
  /** Set sort (resets page to 1). */
  setSort: (sort: CrewSort) => void
  /** Navigate to a different page, preserving filters + sort. */
  setPage: (page: number) => void
  /** Reset everything to defaults (clears all filters, default sort, page 1). */
  clearAll: () => void
  /**
   * OKS-28 — Apply / clear a saved segment.
   *
   * Setting a segment id emits `?segment=<id>` to the URL and DROPS
   * every raw filter param (segment.rules WIN over ad-hoc filters per
   * UX Spec Open-Q6 — exclusive apply). Setting `null` clears the
   * segment AND the filters together (selector reverts to "All crew").
   */
  setSegment: (id: number | null) => void
}

/**
 * Hook exposing typed setters that emit normalized URL params via
 * Inertia partial reload. Caller passes the current props snapshot
 * (filters/sort) so the hook can compute deltas without re-reading
 * `usePage()` on every render.
 *
 * Stats are NEVER refetched by this hook — `RELOAD_KEYS` deliberately
 * omits `stats` (PRD §Acceptance: stats describe the unfiltered crew).
 * Page-level Vitest in F10 asserts the invariant.
 */
export function useCrewListUrl(
  filters: CrewFilters,
  sort: CrewSort,
): UseCrewListUrlReturn {
  const push = useCallback(
    (
      nextFilters: Partial<CrewFilters>,
      nextSort?: Partial<CrewSort>,
      nextPage?: number,
    ) => {
      const merged: CrewFilters = { ...filters, ...nextFilters }
      const mergedSort = { ...sort, ...(nextSort ?? {}) }
      // Setting a filter implicitly clears any applied segment — UX
      // Spec Open-Q6 + the BE precedence rule (segment.rules WIN) make
      // it confusing to leave `?segment=<id>` on the URL while the user
      // edits filters that would be silently overridden BE-side. The
      // FE "clears" segment by passing `undefined` for segmentId here.
      const params = buildCrewListParams(merged, mergedSort, nextPage, null)
      router.get(crewPath(), params, {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      })
    },
    [filters, sort],
  )

  const setFilter = useCallback(
    <K extends keyof CrewFilters>(key: K, value: CrewFilters[K]) => {
      push({ [key]: value } as Partial<CrewFilters>, undefined, 1)
    },
    [push],
  )

  const setSort = useCallback(
    (nextSort: CrewSort) => {
      push({}, nextSort, 1)
    },
    [push],
  )

  const setPage = useCallback(
    (page: number) => {
      push({}, undefined, page)
    },
    [push],
  )

  const clearAll = useCallback(() => {
    router.get(
      crewPath(),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      },
    )
  }, [])

  // OKS-28 — Apply or clear a saved segment.
  //
  // - `id` set: emits `?segment=<id>` (+ sort, + page) and DROPS every
  //   raw filter param. The BE precedence rule (segment.rules WIN)
  //   already overrides ad-hoc filters server-side; mirroring at the
  //   URL keeps `?segment=<id>` shareable and the back-button URL clean.
  // - `id` null: clears the segment AND the filters together (selector
  //   reverts to "All crew"; chip is DOM-absent on next render).
  const setSegment = useCallback(
    (id: number | null) => {
      if (id === null) {
        // Same wire shape as `clearAll` — but the user reached this
        // path via the chip X / "All crew" selector item, so the
        // intent is "drop the segment AND the filter rules it carried".
        // BE serves the unfiltered roster on the next render.
        router.get(
          crewPath(),
          {},
          {
            preserveState: true,
            preserveScroll: true,
            only: [...RELOAD_KEYS],
          },
        )
      } else {
        const params = buildCrewListParams(filters, sort, 1, id)
        router.get(crewPath(), params, {
          preserveState: true,
          preserveScroll: true,
          only: [...RELOAD_KEYS],
        })
      }
    },
    [filters, sort],
  )

  return {
    push,
    setFilter,
    setSort,
    setPage,
    clearAll,
    setSegment,
  }
}
